import { message, Modal, Form, Input, Select, DatePicker, Row, Col, Divider, Typography, Space, Button, Spin, Alert, Radio } from "antd";
import { BankOutlined, CalendarOutlined, FileTextOutlined, NumberOutlined, PlusOutlined, SwapOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import axiosInstance from "../../../variables/api/axiosInstance";
import { getParametre } from "../../../hooks/useHooks";
import { PARAMETERS } from "../../../variables/constants";
import { displayNumbers } from "../../../variables/numbersFunctions";

const { Text, Title } = Typography;
const { TextArea } = Input;

const BANQUES = [
    { label: "ORABANK", value: "ORABANK" },
    { label: "IB BANK", value: "IB BANK" },
    { label: "UTB", value: "UTB" },
];

// Nom du bénéficiaire (établissement le cas échéant), pour le bandeau récap.
// Les listings passent parfois le bénéficiaire en simple texte (DTO plat).
const beneficiaireNom = (d) =>
    d?.beneficiaireMoral?.nom
    || (typeof d?.beneficiaire === "string" ? d.beneficiaire : null)
    || (d?.beneficiaire?.prenom ? `${d?.beneficiaire?.nom} ${d?.beneficiaire?.prenom}` : d?.beneficiaire?.nom)
    || "—";

const AssignCheque = (prop) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // Mode de règlement comptable : "CHEQUE" (défaut) ou "VIREMENT". Un virement réutilise les mêmes
    // champs que le chèque (numeroCheque = référence, dateEmissionCheque = date d'exécution, banque) ;
    // seul le libellé change à l'écran et modePaiement est transmis au backend.
    const [modePaiement, setModePaiement] = useState("CHEQUE");
    const isVirement = modePaiement === "VIREMENT";

    const [numeroCheque, setNumeroCheque] = useState("");
    const [dateEmissionCheque, setDateEmissionCheque] = useState(dayjs());
    // Date de RÈGLEMENT (comptable). Éditable seulement en mode ÉDITION (chèque déjà assigné), HSI089.
    const [dateReglement, setDateReglement] = useState(null);
    const [banque, setBanque] = useState(null);
    // Banques chargées depuis le paramétrage (type BANQUE) ; repli sur la liste figée en cas d'échec.
    const [banques, setBanques] = useState(BANQUES);

    // Formule de conclusion (FACULTATIVE) choisie ici, à l'édition de la quittance.
    const [formules, setFormules] = useState([]);
    const [formulesLoading, setFormulesLoading] = useState(false);
    const [formuleSelected, setFormuleSelected] = useState(undefined);
    const [formuleText, setFormuleText] = useState("");
    const [formuleNewName, setFormuleNewName] = useState("");
    const formuleInputRef = useRef(null);

    useEffect(() => {
        setOpen(prop.open);
    }, [prop.open]);

    // Chargement des formules de conclusion paramétrées.
    useEffect(() => {
        if (!prop.open) return;
        let active = true;
        setFormulesLoading(true);
        getParametre(PARAMETERS.formuleCloture)
            .then((res) => { if (active) setFormules(res?.data || []); })
            .catch(() => { if (active) setFormules([]); })
            .finally(() => { if (active) setFormulesLoading(false); });
        return () => { active = false; };
    }, [prop.open]);

    // Banques chargées depuis le paramétrage (Paramétrage → Banques, type BANQUE). La modale
    // affichait auparavant une liste FIGÉE de 3 banques → une banque ajoutée en paramétrage n'y
    // apparaissait pas. On mappe le libellé du paramètre ; repli sur la liste figée si vide/échec.
    useEffect(() => {
        if (!prop.open) return;
        let active = true;
        getParametre(PARAMETERS.banque)
            .then((res) => {
                if (!active) return;
                const opts = (res?.data || [])
                    .map((p) => (p?.libelleParametre || "").trim())
                    .filter(Boolean)
                    .filter((v, i, arr) => arr.indexOf(v) === i) // dédup
                    .map((v) => ({ label: v, value: v }));
                setBanques(opts.length ? opts : BANQUES);
            })
            .catch(() => { if (active) setBanques(BANQUES); });
        return () => { active = false; };
    }, [prop.open]);

    // Mode ÉDITION (modification du chèque déjà assigné) : pré-remplir avec les infos actuelles.
    useEffect(() => {
        if (prop.open && prop.editMode && prop.decaissement) {
            setModePaiement(prop.decaissement.modePaiement === "VIREMENT" ? "VIREMENT" : "CHEQUE");
            setNumeroCheque(prop.decaissement.numeroCheque || "");
            setBanque(prop.decaissement.banque || null);
            setDateEmissionCheque(prop.decaissement.dateEmissionCheque
                ? dayjs(prop.decaissement.dateEmissionCheque) : dayjs());
            setDateReglement(prop.decaissement.dateReglement
                ? dayjs(prop.decaissement.dateReglement) : null);
            setFormuleText(prop.decaissement.formuleDeFin || "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prop.open, prop.editMode, prop.decaissement]);

    // La valeur d'option est le LIBELLÉ, pas le texte : les formules de clôture font
    // plusieurs centaines de caractères et s'afficheraient en entier dans le champ fermé.
    const onFormuleSelect = (value) => {
        setFormuleSelected(value);
        const selected = formules.find((f) => f.libelleParametre === value);
        setFormuleText(selected?.valeur || "");
    };

    const addFormule = (e) => {
        e?.preventDefault?.();
        const name = formuleNewName?.trim();
        if (!name) return;
        setFormules((prev) => [...prev, { libelleParametre: name, valeur: name }]);
        setFormuleNewName("");
        formuleInputRef.current?.focus();
    };

    const resetState = () => {
        setModePaiement("CHEQUE");
        setNumeroCheque("");
        setBanque(null);
        setFormuleSelected(undefined);
        setFormuleText("");
        setFormuleNewName("");
    };

    // Assigne le chèque à un règlement unique. Si le chèque est déjà utilisé sur d'autres
    // règlements, le backend répond 409 (CHEQUE_DEJA_ASSIGNE) → on demande confirmation, puis on
    // réessaie avec forcerChequePartage=true. Retourne la réponse, ou null si l'utilisateur annule.
    const assignerChequeSimple = async (decaissement, formuleDeFin) => {
        const chequeDTO = {
            numeroCheque: numeroCheque.trim(),
            banque,
            dateCheque: dateEmissionCheque.format("YYYY-MM-DD"),
            montantCheque: decaissement.montantGlobal,
            formuleDeFin,
            modePaiement,
            forcerChequePartage: false,
        };
        const put = (dto) => axiosInstance.put(
            `/api/decaissement/assigner-cheque/${decaissement.codeId}`,
            JSON.stringify(dto),
            { headers: { "Content-Type": "application/json" } }
        );
        try {
            return await put(chequeDTO);
        } catch (err) {
            const data = err?.response?.data;
            if (err?.response?.status === 409 && data?.code === "CHEQUE_DEJA_ASSIGNE") {
                const confirmed = await new Promise((resolve) => {
                    Modal.confirm({
                        title: "Chèque déjà utilisé",
                        content: `Ce chèque est déjà assigné à ${data.count} règlement(s). Voulez-vous quand même l'utiliser pour ce règlement (paiement partagé) ?`,
                        okText: "Oui, utiliser ce chèque",
                        cancelText: "Annuler",
                        onOk: () => resolve(true),
                        onCancel: () => resolve(false),
                    });
                });
                if (!confirmed) return null;
                return await put({ ...chequeDTO, forcerChequePartage: true });
            }
            throw err;
        }
    };

    const handleSubmit = async () => {
        const group = Array.isArray(prop?.decaissements) && prop.decaissements.length ? prop.decaissements : null;
        const decaissement = prop?.decaissement;
        if (!group && !decaissement) {
            message.error("Erreur lors du décaissement");
            return;
        }
        // Chèque : n° d'au moins 5 caractères (contrôle historique). Virement : la référence peut être
        // courte/alphanumérique → on exige seulement qu'elle soit renseignée.
        if (isVirement) {
            if (!numeroCheque || !numeroCheque.trim()) {
                message.error("Référence du virement obligatoire");
                return;
            }
        } else if (!numeroCheque || numeroCheque.trim().length < 5) {
            message.error("Numéro de chèque obligatoire (5 caractères min)");
            return;
        }
        if (!banque) {
            message.error("Banque obligatoire");
            return;
        }

        const formuleDeFin = formuleText?.trim() ? formuleText.trim() : null;
        setLoading(true);
        try {
            let res;
            if (group) {
                const codeIds = group.filter((d) => d?.etatDecaissement === "VALIDE").map((d) => d.codeId);
                if (!codeIds.length) {
                    message.error("Aucune ligne validée à régler dans ce groupe");
                    setLoading(false);
                    return;
                }
                const params = new URLSearchParams({
                    numeroCheque: numeroCheque.trim(),
                    banque,
                    dateCheque: dateEmissionCheque.format("YYYY-MM-DD"),
                    modePaiement,
                });
                if (formuleDeFin) params.append("formuleDeFin", formuleDeFin);
                res = await axiosInstance.put(
                    `/api/decaissement/assigner-cheque-multiple?${params.toString()}`,
                    JSON.stringify(codeIds),
                    { headers: { "Content-Type": "application/json" } }
                );
            } else if (prop.editMode) {
                // Modification des infos du chèque d'un règlement déjà en attente d'encaissement (HSI089).
                res = await axiosInstance.put(
                    `/api/decaissement/modifier-cheque/${decaissement.codeId}`,
                    JSON.stringify({
                        numeroCheque: numeroCheque.trim(),
                        banque,
                        dateCheque: dateEmissionCheque.format("YYYY-MM-DD"),
                        montantCheque: decaissement.montantGlobal,
                        formuleDeFin,
                        modePaiement,
                        // Date de règlement modifiée (facultatif). Le backend applique la garde
                        // « mois non cédé » (HSI089) et refuse si déjà cédé / date future.
                        dateReglement: dateReglement ? dateReglement.format("YYYY-MM-DD") : null,
                    }),
                    { headers: { "Content-Type": "application/json" } }
                );
            } else {
                res = await assignerChequeSimple(decaissement, formuleDeFin);
                if (!res) { setLoading(false); return; } // partage refusé par l'utilisateur
            }

            const motMode = isVirement ? "Virement" : "Chèque";
            message.success(prop.editMode ? `${motMode} modifié avec succès` : `${motMode} assigné avec succès`);
            setOpen(false);
            resetState();
            if (prop.onSuccess) prop.onSuccess(res.data);
            else window.location.reload();
        } catch (err) {
            console.log(err);
            message.error(err?.response?.data?.message || "Erreur lors de l'assignation du chèque");
        } finally {
            setLoading(false);
        }
    };

    const headDec = prop?.decaissement
        || (Array.isArray(prop?.decaissements) ? prop.decaissements[0] : null);
    const totalGroupe = Array.isArray(prop?.decaissements)
        ? prop.decaissements.reduce((s, d) => s + (parseFloat(d?.montantGlobal) || 0), 0)
        : (parseFloat(headDec?.montantGlobal) || 0);

    return (
        <Modal
            title={prop.editMode ? "Modifier le chèque" : (prop?.decaissement?.encaissement ? "Enregistrer l'encaissement" : "Éditer la quittance")}
            confirmLoading={loading}
            centered
            open={open}
            onOk={handleSubmit}
            onCancel={prop.onCancel}
            okText={isVirement ? "Valider le virement" : "Valider le chèque"}
            cancelText="Annuler"
            width={640}
            styles={{ body: { paddingTop: 8 } }}
        >
            {/* Bandeau récapitulatif (bénéficiaire + montant) */}
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 20, borderRadius: 8 }}
                message={
                    <Space direction="vertical" size={0}>
                        <Text strong>{beneficiaireNom(headDec)}</Text>
                        <Text type="secondary">
                            Montant à régler : <Text strong style={{ color: "#1677ff" }}>{displayNumbers(Math.trunc(totalGroupe))} FCFA</Text>
                            {Array.isArray(prop?.decaissements) && prop.decaissements.length > 1
                                ? ` · ${prop.decaissements.length} lignes regroupées` : ""}
                        </Text>
                    </Space>
                }
            />

            <Form layout="vertical" requiredMark="optional">
                {/* Mode de règlement comptable. En édition on n'autorise pas la bascule chèque↔virement
                    (le mode d'un règlement déjà assigné ne se change pas à l'édition). */}
                <Form.Item label={<Text strong><SwapOutlined /> Mode de règlement</Text>} style={{ marginBottom: 16 }}>
                    <Radio.Group
                        optionType="button"
                        buttonStyle="solid"
                        value={modePaiement}
                        onChange={(e) => setModePaiement(e.target.value)}
                        disabled={prop.editMode}
                        options={[
                            { label: "Chèque", value: "CHEQUE" },
                            { label: "Virement", value: "VIREMENT" },
                        ]}
                    />
                </Form.Item>
                <Title level={5} style={{ marginTop: 0, marginBottom: 16, fontSize: 14, color: "#242166" }}>
                    <BankOutlined /> {isVirement ? "Informations du virement" : "Informations du chèque"}
                </Title>
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item label={isVirement ? "Référence du virement" : "Numéro de chèque"} required>
                            <Input
                                prefix={<NumberOutlined style={{ color: "#bfbfbf" }} />}
                                placeholder={isVirement ? "Référence du virement" : "Min. 5 chiffres"}
                                value={numeroCheque}
                                onChange={(e) => setNumeroCheque(e.target.value)}
                                status={!isVirement && numeroCheque && numeroCheque.trim().length < 5 ? "warning" : ""}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item label={isVirement ? "Date d'exécution" : "Date d'émission"}>
                            <DatePicker
                                style={{ width: "100%" }}
                                format="DD/MM/YYYY"
                                value={dateEmissionCheque}
                                onChange={(date) => setDateEmissionCheque(date)}
                                maxDate={dayjs()}
                                allowClear={false}
                                suffixIcon={<CalendarOutlined />}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item label="Banque" required>
                            <Select
                                placeholder="Sélectionner la banque"
                                value={banque}
                                onChange={setBanque}
                                showSearch
                                optionFilterProp="label"
                                // Fusionne la banque déjà enregistrée si elle n'est plus dans le paramétrage,
                                // pour qu'elle reste affichée en mode édition.
                                options={banque && !banques.some((b) => b.value === banque)
                                    ? [...banques, { label: banque, value: banque }]
                                    : banques}
                                suffixIcon={<BankOutlined />}
                            />
                        </Form.Item>
                    </Col>
                    {/* Date de règlement : uniquement en ÉDITION (chèque déjà assigné), sous HSI089.
                        Le backend refuse une date future / un mois déjà cédé / un règlement déjà cédé. */}
                    {prop.editMode && (
                        <Col xs={24} md={12}>
                            <Form.Item label="Date de règlement"
                                help="Modifiable sous habilitation. Refusée si le mois est déjà cédé.">
                                <DatePicker
                                    style={{ width: "100%" }}
                                    format="DD/MM/YYYY"
                                    value={dateReglement}
                                    onChange={(date) => setDateReglement(date)}
                                    maxDate={dayjs()}
                                    placeholder="Inchangée"
                                    suffixIcon={<CalendarOutlined />}
                                />
                            </Form.Item>
                        </Col>
                    )}
                </Row>

                <Divider style={{ margin: "8px 0 16px" }} />

                <Title level={5} style={{ marginTop: 0, marginBottom: 4, fontSize: 14, color: "#242166" }}>
                    <FileTextOutlined /> Formule de conclusion <Text type="secondary" style={{ fontWeight: 400 }}>(facultatif)</Text>
                </Title>
                <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 12 }}>
                    Cette formule s'imprimera sur la quittance. Laissez vide pour ne rien afficher.
                </Text>
                <Spin spinning={formulesLoading}>
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Choisir une formule existante…"
                        style={{ width: "100%" }}
                        value={formuleSelected}
                        onChange={onFormuleSelect}
                        options={formules?.map((f) => ({ label: f?.libelleParametre, value: f?.libelleParametre })) || []}
                        optionRender={(opt) => {
                            const texte = formules.find((f) => f?.libelleParametre === opt.value)?.valeur;
                            return (
                                <div style={{ whiteSpace: "normal", padding: "2px 0" }}>
                                    <div style={{ fontWeight: 600 }}>{opt.label}</div>
                                    {texte && texte !== opt.label && (
                                        <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.35 }}>
                                            {texte.length > 160 ? `${texte.slice(0, 160)}…` : texte}
                                        </div>
                                    )}
                                </div>
                            );
                        }}
                        listHeight={320}
                        dropdownRender={(menu) => (
                            <>
                                {menu}
                                <Divider style={{ margin: "8px 0" }} />
                                <Space style={{ padding: "0 8px 4px" }}>
                                    <Input
                                        placeholder="Nouvelle formule"
                                        ref={formuleInputRef}
                                        value={formuleNewName}
                                        onChange={(e) => setFormuleNewName(e.target.value)}
                                        onPressEnter={addFormule}
                                    />
                                    <Button type="text" icon={<PlusOutlined />} onClick={addFormule}>
                                        Ajouter
                                    </Button>
                                </Space>
                            </>
                        )}
                    />
                    {/* autoSize : les formules de clôture font 4 à 6 lignes — 3 lignes fixes
                        obligeaient à scroller pour relire un texte à portée juridique. */}
                    <TextArea
                        autoSize={{ minRows: 4, maxRows: 12 }}
                        value={formuleText}
                        onChange={(e) => setFormuleText(e.target.value)}
                        placeholder="Saisir ou ajuster la formule à imprimer sur la quittance…"
                        style={{ marginTop: 10 }}
                    />
                </Spin>
            </Form>
        </Modal>
    );
};

AssignCheque.propTypes = {
    open: PropTypes.bool,
    decaissement: PropTypes.object,
    decaissements: PropTypes.array,
    sinistre: PropTypes.object,
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func,
};

export default AssignCheque;
